import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import wsManager, { WS_STATES } from '@/services/websocketManager';

const WebSocketContext = createContext(null);

export function WebSocketProvider({ children }) {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState(WS_STATES.DISCONNECTED);
  const [notifications, setNotifications] = useState([]);
  const initializedRef = useRef(false);

  // Handle incoming messages - defined before useEffect to avoid dependency issues
  const handleMessage = useCallback((data) => {
    // Handle different message types
    if (data.type === 'connected' || data.type === 'heartbeat') {
      return;
    }

    // Add to notifications list
    setNotifications((prev) => [data, ...prev].slice(0, 50));

    // Show toast notification
    if (data.title && data.message) {
      const toastType = getToastType(data.type);
      toast[toastType](data.title, {
        description: data.message,
        action: data.data?.job_id ? {
          label: 'View',
          onClick: () => {
            window.location.href = `/jobs?job=${data.data.job_id}`;
          }
        } : undefined
      });
    }
  }, []);

  // Initialize WebSocket manager when token is available
  useEffect(() => {
    if (initializedRef.current) return;
    
    const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
    if (!token) return;
    
    initializedRef.current = true;
    
    // Initialize the centralized WebSocket manager
    wsManager.initialize({ token });
    
    // Subscribe to state changes
    const unsubscribe = wsManager.subscribeToState((newState) => {
      setConnectionState(newState);
      setIsConnected(newState === WS_STATES.CONNECTED);
    });
    
    // Subscribe to messages
    const unsubscribeMessages = wsManager.subscribe('notifications', handleMessage);
    
    return () => {
      unsubscribe();
      unsubscribeMessages();
    };
  }, [handleMessage]);

  // Disconnect function using centralized manager
  const disconnect = useCallback(() => {
    wsManager.disconnect();
    setIsConnected(false);
  }, []);

  // Send message function using centralized manager
  const sendMessage = useCallback((message) => {
    return wsManager.send(message);
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Reconnect function
  const reconnect = useCallback(() => {
    const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
    wsManager.reset(token);
  }, []);

  const value = {
    isConnected,
    connectionState,
    notifications,
    disconnect,
    reconnect,
    sendMessage,
    clearNotifications,
    unreadCount: notifications.filter(n => !n.read).length,
    // Expose manager for advanced usage
    getMetrics: () => wsManager.getMetrics()
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}

function getToastType(notificationType) {
  switch (notificationType) {
    case 'job_assigned':
    case 'client_submission':
      return 'info';
    case 'job_status_changed':
    case 'visit_scheduled':
      return 'success';
    case 'system_alert':
      return 'warning';
    default:
      return 'info';
  }
}

export default WebSocketContext;
